import Drawer from "@mui/material/Drawer";

import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { Box, Collapse, useMediaQuery } from "@mui/material";
import KeyIcon from "@mui/icons-material/Key";
import { useEffect, useState } from "react";
import { useAccount } from "@gu-corp/gu-wallet-connector";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useRoles } from "../../providers/ContractProvider";
import theme from "../../theme";
import useAuthorized from "../../hooks/useAuthorized";
import {
  useMobileSidebarState,
  useMobileSidebarVisibleState,
} from "../../providers/mobile-sidebar/MobileSidebarProvider";
import Unauthenticated from "../Layout/UnAuthenticated";
import { Link, Outlet, useLocation } from "react-router-dom";
import Authenticating from "../Layout/Authenticating";

const DRAWER_WIDTH = 240;
const DRAWER_MEDIUM_WIDTH = 200;

const LINKS = [
  {
    text: "Ownerships",
    href: "/key-keeper/ownerships",
    icon: FormatListBulletedIcon,
  },
  { text: "Key Keepers", href: "/key-keeper/key-keepers", icon: KeyIcon },
];

const PROPOSAL_LINKS = [
  {
    text: "Cancel Lockup",
    href: "/key-keeper/proposals/cancel-lockup",
  },
  {
    text: "Extend Lockup Period",
    href: "/key-keeper/proposals/extend-lockup-period",
  },
  {
    text: "Grant Key Keeper",
    href: "/key-keeper/proposals/grant-key-keeper",
  },
  {
    text: "Revoke Key Keeper",
    href: "/key-keeper/proposals/revoke-key-keeper",
  },
  {
    text: "Transfer Ownership",
    href: "/key-keeper/proposals/transfer-ownership",
  },
  {
    text: "Upgradable",
    href: "/key-keeper/proposals/upgradable",
  },
];

export default function KeyKeeperLayout() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(!open);
  };
  const { KEY_KEEPER_ROLE } = useRoles();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { account } = useAccount();
  const { authorized, loading } = useAuthorized(KEY_KEEPER_ROLE, account);

  const { setVisible } = useMobileSidebarVisibleState();
  useEffect(() => {
    if (authorized) {
      setVisible(true);
    }
  }, [authorized, setVisible]);
  const { open: mobileOpen } = useMobileSidebarState();

  if (loading) {
    return <Authenticating />;
  }

  if (!authorized) {
    return <Unauthenticated />;
  }

  return (
    <>
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            top: ["48px", "56px", "64px"],
            height: "auto",
            bottom: 0,
          },
          display: { xs: "block", sm: "none" },
        }}
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Divider />
        <List>
          {LINKS.map(({ text, href, icon: Icon }) => (
            <ListItem key={href} disablePadding>
              <ListItemButton
                component={Link}
                to={href}
                selected={href == location.pathname}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItemButton onClick={handleClick}>
            <ListItemIcon>
              <HowToVoteIcon />
            </ListItemIcon>
            <ListItemText primary="Proposals" />
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {PROPOSAL_LINKS.map((link) => (
                <ListItemButton
                  component={Link}
                  to={link.href}
                  sx={{ pl: 4 }}
                  selected={link.href == location.pathname}
                >
                  <ListItemText primary={link.text} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </List>
        <Divider sx={{ mt: "auto" }} />
      </Drawer>
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isMediumScreen ? DRAWER_MEDIUM_WIDTH : DRAWER_WIDTH,
            boxSizing: "border-box",
            top: ["48px", "56px", "64px"],
            height: "auto",
            bottom: 0,
          },
          display: { xs: "none", sm: "block" },
        }}
        variant="permanent"
        anchor="left"
        open={mobileOpen}
      >
        <Divider />
        <List>
          {LINKS.map(({ text, href, icon: Icon }) => (
            <ListItem key={href} disablePadding>
              <ListItemButton
                component={Link}
                to={href}
                selected={href == location.pathname}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItemButton onClick={handleClick}>
            <ListItemIcon>
              <HowToVoteIcon />
            </ListItemIcon>
            <ListItemText primary="Proposals" />
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {PROPOSAL_LINKS.map((link) => (
                <ListItemButton
                  key={link.href}
                  component={Link}
                  to={link.href}
                  sx={{ pl: 4 }}
                  selected={link.href == location.pathname}
                >
                  <ListItemText primary={link.text} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </List>
        <Divider sx={{ mt: "auto" }} />
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          ml: `${
            isSmallScreen
              ? 0
              : isMediumScreen
              ? DRAWER_MEDIUM_WIDTH
              : DRAWER_WIDTH
          }px`,
          p: [3],
        }}
      >
        <Outlet />
      </Box>
    </>
  );
}
